import { writeEntity } from '@/lib/entity-api';
import { trackBillPaymentReceived, trackBillPaymentUpdated, trackFormSubmission } from '@/lib/posthog-events';

export interface BillPaymentInput {
  project_id: string;
  /** Optional: a payment can be recorded without a bill. */
  bill_id: string | null;
  vendor_id: string | null;
  payment_number: string | null;
  payment_date: string;
  status: string;
  amount: number;
  notes: string | null;
  /** A key for ONE logical save: sent again (double click, a retry after a lost answer) the server answers with the payment it already saved. */
  client_request_id?: string;
}

/** Bill payment writes (generic entity API). The server checks the payment against the bill's balance and settles the bill's status. */
export const BillPaymentsService = {
  create(payload: BillPaymentInput) {
    return writeEntity<any>('api/entities/bill_payment', 'POST', payload, 'Failed to create payment');
  },
  update(id: string, payload: Partial<BillPaymentInput>) {
    return writeEntity<any>(`api/entities/bill_payment/${id}`, 'PUT', payload, 'Failed to update payment');
  },
  async remove(id: string): Promise<void> {
    // deleting a payment takes its attachments with it (server cascade) and re-settles its bill
    await writeEntity<any>(`api/entities/bill_payment/${id}`, 'DELETE', {}, 'Failed to delete payment');
  },
};

/**
 * Creates (no `id`) or updates (`id`) a payment in ONE request and tracks the outcome. The single save path of the Payments
 * tab and of the Bill form's "Record Payment".
 */
export async function saveBillPayment(
  { id, ...data }: Partial<BillPaymentInput> & { id?: string },
  who: { companyId: string; userId: string }
) {
  const isUpdate = !!id;
  try {
    const saved = isUpdate
      ? await BillPaymentsService.update(id as string, data)
      : await BillPaymentsService.create(data as BillPaymentInput);
    const tracked = {
      payment_id: saved.id,
      payment_number: data.payment_number,
      project_id: data.project_id,
      bill_id: data.bill_id,
      amount: data.amount,
      status: data.status,
      payment_date: data.payment_date,
      attachment_count: 0,
      company_id: who.companyId,
    };
    if (isUpdate) trackBillPaymentUpdated({ ...tracked, updated_by: who.userId });
    else trackBillPaymentReceived({ ...tracked, created_by: who.userId });
    trackFormSubmission({ form_type: isUpdate ? 'bill_payment_update' : 'bill_payment_creation', success: true });
    return saved;
  } catch (e: any) {
    trackFormSubmission({ form_type: isUpdate ? 'bill_payment_update' : 'bill_payment_creation', success: false, error_message: e?.message || 'Failed to save payment' });
    throw e;
  }
}
